/**
 * Formatting and local-date helpers for the UI.
 *
 * The API works in the user's configured timezone, which is not necessarily the
 * browser's, so local dates are derived from that preference rather than from
 * `Date` defaults. Everything here keeps dates as `YYYY-MM-DD` strings, which is
 * exactly what the API expects and avoids a timezone round trip.
 */

export function todayInTimeZone(timeZone: string, now: Date = new Date()): string {
  return formatLocalDate(now, timeZone);
}

export function formatLocalDate(instant: Date, timeZone: string): string {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(instant);

  const get = (type: string): string => parts.find((part) => part.type === type)?.value ?? '';
  return `${get('year')}-${get('month')}-${get('day')}`;
}

/** Shifts a YYYY-MM-DD string by whole days, staying in calendar space. */
export function addDays(localDate: string, days: number): string {
  const [year = 0, month = 1, day = 1] = localDate.split('-').map(Number);
  const shifted = new Date(Date.UTC(year, month - 1, day + days));

  return [
    String(shifted.getUTCFullYear()).padStart(4, '0'),
    String(shifted.getUTCMonth() + 1).padStart(2, '0'),
    String(shifted.getUTCDate()).padStart(2, '0'),
  ].join('-');
}

/** "Heute", "Gestern", otherwise "Mo., 12. Aug." */
export function formatDateLabel(localDate: string, timeZone: string): string {
  const today = todayInTimeZone(timeZone);
  if (localDate === today) return 'Heute';
  if (localDate === addDays(today, -1)) return 'Gestern';
  if (localDate === addDays(today, 1)) return 'Morgen';

  const [year = 0, month = 1, day = 1] = localDate.split('-').map(Number);
  return new Intl.DateTimeFormat('de-DE', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    timeZone: 'UTC',
  }).format(new Date(Date.UTC(year, month - 1, day)));
}

export function formatTime(isoString: string, timeZone: string): string {
  return new Intl.DateTimeFormat('de-DE', {
    hour: '2-digit',
    minute: '2-digit',
    timeZone,
  }).format(new Date(isoString));
}

/** A YYYY-MM-DD as "24. Nov. 2026". */
export function formatLongDate(localDate: string): string {
  const [year = 0, month = 1, day = 1] = localDate.split('-').map(Number);
  return new Intl.DateTimeFormat('de-DE', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(new Date(Date.UTC(year, month - 1, day)));
}

/** A duration in milliseconds as "11 h 33 min", or "45 min" under an hour. */
export function formatDuration(ms: number): string {
  const totalMinutes = Math.max(0, Math.round(ms / 60_000));
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  if (hours === 0) return `${minutes} min`;
  return `${hours} h ${minutes} min`;
}

const numberFormatters = new Map<number, Intl.NumberFormat>();

function numberFormatter(decimals: number): Intl.NumberFormat {
  let formatter = numberFormatters.get(decimals);
  if (!formatter) {
    formatter = new Intl.NumberFormat('de-DE', {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    });
    numberFormatters.set(decimals, formatter);
  }
  return formatter;
}

export function formatNumber(value: number, decimals = 0): string {
  return numberFormatter(decimals).format(value);
}

/** Grams with one decimal, dropping a trailing ",0". */
export function formatGrams(value: number): string {
  const rounded = Math.round(value * 10) / 10;
  return Number.isInteger(rounded) ? formatNumber(rounded, 0) : formatNumber(rounded, 1);
}

export function formatKcal(value: number): string {
  return formatNumber(Math.round(value));
}

export const MEAL_LABELS: Record<string, string> = {
  breakfast: 'Frühstück',
  lunch: 'Mittagessen',
  dinner: 'Abendessen',
  snack: 'Snacks',
};
